import {
  Center,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  List,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
} from '@chakra-ui/react';
import { RequestStatus, TSRequest, TypesenseCollection } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { subWeeks } from 'date-fns';
import { isEqual } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { HiCog6Tooth, HiMagnifyingGlass, HiXMark } from 'react-icons/hi2';

import { trpc } from '../config/trpc';
import { useDebounce } from '../hooks/useDebounce';
import { usePrevious } from '../hooks/usePrevious';
import { defaultRequestFilters, useRequestFilters } from '../hooks/useRequestFilters';
import { useRequestSort } from '../hooks/useRequestSort';
import { SearchRequestResponse, useSearchRequests } from '../hooks/useSearchRequests';
import { PelicargoLogo } from './PelicargoLogo';
import { RequestFilter } from './RequestFilter';
import { RequestListItem } from './RequestListItem';
import { RequestSort } from './RequestSort';

const ACTIVE_STATUSES: RequestStatus[] = [
  RequestStatus.New,
  RequestStatus.QuotingInProgress,
  RequestStatus.QuotingPending,
  RequestStatus.BookingRequested,
  RequestStatus.BookingPending,
  RequestStatus.BookingChange,
  RequestStatus.BookingCancel,
];

export const isRequestActive = (iRequest: TSRequest) => {
  return ACTIVE_STATUSES.includes(iRequest.request_status as RequestStatus);
};

const activeFilter = { status: ACTIVE_STATUSES };

export const RequestList = () => {
  const toast = useToast();
  const { mutateAsync: resetTypesense, isLoading: isResetting } = trpc.resetIndexes.useMutation();

  const {
    query,
    setQuery,
    page: allTabPage,
    setPage: setAllTabPage,
    filters,
    handleCheckboxFilterChange,
    handleRadioFilterChange,
    resetFilters,
    resetSingleFilter,
    incrementPage: incrementAllPage,
  } = useRequestFilters();

  const { sort: sort, setSort: setSort } = useRequestSort();

  const { page: activeTabPage, incrementPage: incrementActivePage } = useRequestFilters();
  const prevFilters = usePrevious(filters);

  const [currentTab, setCurrentTab] = useState(0);
  const debouncedQuery = useDebounce(query, 300);

  const {
    results: activeData,
    nextPage: nextActivePage,
    updateRequest: updateActiveRequest,
  } = useSearchRequests({
    search: debouncedQuery,
    skip: activeTabPage,
    filters: activeFilter,
    sort,
    take: 20,
    incrementPage: incrementActivePage,
  });

  const {
    results: allData,
    nextPage: nextAllPage,
    updateRequest: updateAllRequest,
  } = useSearchRequests({
    search: debouncedQuery,
    skip: allTabPage,
    take: 20,
    sort,
    filters,
    incrementPage: incrementAllPage,
  });

  // A request can live in both the Active and All caches; apply live updates to
  // both so switching tabs never re-seeds a list item from stale search data.
  const updateRequest = useCallback(
    (id: string, data: Parameters<typeof updateActiveRequest>[1]) => {
      updateActiveRequest(id, data);
      updateAllRequest(id, data);
    },
    [updateActiveRequest, updateAllRequest],
  );

  useEffect(() => {
    const filtersChanged = (prevFilters && !isEqual(prevFilters, filters)) || debouncedQuery !== '';
    if (filtersChanged) {
      setAllTabPage(1);
    }
  }, [debouncedQuery, filters, prevFilters, setAllTabPage]);

  useEffect(() => {
    if (currentTab !== 1 && !isEqual(filters, defaultRequestFilters)) {
      setCurrentTab(1);
    }
  }, [currentTab, filters]);

  const clearSearchAndFilters = () => {
    setQuery('');
    resetFilters();
  };

  const handleTabChange = (index: number) => {
    setCurrentTab(index);
    if (index === 0) clearSearchAndFilters();
  };

  const handleResetTypesense = (collectionName: TypesenseCollection) => async () => {
    try {
      toast({
        title: 'Resetting Typesense',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
      const { message } = await resetTypesense({ collections: [collectionName] });

      toast({
        title: 'Typesense reset',
        description: message,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Typesense reset failed',
        description: error?.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleQueryChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (currentTab !== 1) setCurrentTab(1);
    setQuery(event.target.value);
  };

  const menuIcon = isResetting ? <Spinner size="xs" /> : <HiCog6Tooth />;
  const typesenseIcon = isResetting ? <Spinner size="sm" /> : <HiMagnifyingGlass />;
  const hasSearchValue = !!query;
  const { isLoading: isLightResetting, mutateAsync: lightRequestReset } =
    trpc.recalculateRequestClassification.useMutation();

  const handleLightReset = useCallback(
    async (weeks: number) => {
      await lightRequestReset({
        from: subWeeks(new Date(), weeks),
      });
      toast({
        title: 'Typesense reset',
        description: 'Reset the last week of requests!',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    },
    [lightRequestReset, toast],
  );

  return (
    <Stack borderRightWidth={1} h="100vh" w="300px" overflow="hidden">
      <Stack direction="row" spacing={4} alignItems="center" justifyContent="space-between" p={2}>
        <Stack direction="row" justify="space-between" w="full">
          <PelicargoLogo pl={1} top="2px" position="relative" w="140px" />
          <Menu>
            <MenuButton as={IconButton} aria-label="Settings" size="sm" icon={menuIcon} variant="ghost" />
            <MenuList>
              <MenuGroup title="Typesense Collection Reset">
                {Object.keys(TypesenseCollection).map((node) => {
                  return (
                    <MenuItem
                      key={node}
                      onClick={handleResetTypesense(TypesenseCollection[node])}
                      icon={typesenseIcon}
                      isDisabled={isResetting}
                    >
                      Reset {node}
                    </MenuItem>
                  );
                })}
                <MenuItem onClick={() => handleLightReset(1)} icon={typesenseIcon} isDisabled={isLightResetting}>
                  Reset 1 week of requests
                </MenuItem>
                <MenuItem onClick={() => handleLightReset(4)} icon={typesenseIcon} isDisabled={isLightResetting}>
                  Reset 1 month of requests
                </MenuItem>
              </MenuGroup>
            </MenuList>
          </Menu>
        </Stack>
      </Stack>
      <Stack h="full" w="full" overflowY="auto" p={2}>
        <Stack>
          <Stack direction="row" align="center">
            <InputGroup size="md">
              <Input
                type="text"
                autoComplete="off"
                variant="filled"
                placeholder="Type to filter"
                value={query}
                onChange={handleQueryChange}
              />
              <Show if={hasSearchValue}>
                <InputRightElement width="2.7rem">
                  <IconButton
                    aria-label="Clear"
                    onClick={clearSearchAndFilters}
                    icon={<HiXMark />}
                    size="xs"
                    variant="ghost"
                  />
                </InputRightElement>
              </Show>
            </InputGroup>
            <RequestFilter
              filters={filters}
              handleCheckboxFilterChange={handleCheckboxFilterChange}
              handleRadioFilterChange={handleRadioFilterChange}
              resetFilters={resetFilters}
              resetSingleFilter={resetSingleFilter}
            />
            <RequestSort sort={sort} setSort={setSort} />
          </Stack>
          <Tabs colorScheme="brand" size="sm" mt={2} isLazy index={currentTab} onChange={handleTabChange}>
            <TabList>
              <Tab w="full">{activeData?.filtered === null ? '' : activeData?.filtered} Active</Tab>
              <Tab w="full">
                {isEqual(filters, defaultRequestFilters) && !query ? 'All' : `${allData?.filtered ?? '...'} Filtered`}
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel p="0">
                <RequestTabPanel
                  items={activeData?.requests ?? []}
                  isLastPage={!activeData?.hasMore}
                  showMore={nextActivePage}
                  updateRequest={updateRequest}
                  hideInactive
                />
              </TabPanel>
              <TabPanel p="0">
                <RequestTabPanel
                  items={allData?.requests ?? []}
                  isLastPage={!allData?.hasMore}
                  showMore={nextAllPage}
                  updateRequest={updateRequest}
                  hideInactive={false}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Stack>
      </Stack>
    </Stack>
  );
};

type RequestTabPanelProps = {
  items: SearchRequestResponse['results']['requests'];
  isLastPage?: boolean;
  showMore: () => void;
  updateRequest: SearchRequestResponse['updateRequest'];
  hideInactive: boolean;
};

const RequestTabPanel = ({
  items = [],
  isLastPage = true,
  showMore,
  updateRequest,
  hideInactive,
}: RequestTabPanelProps) => {
  // One call for the whole list — the set of requests with an email awaiting
  // review is small, and asking per row would be an N+1 across a paginated list.
  const { data: pendingDrafts } = trpc.getPendingDraftRequestIds.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });
  const pendingDraftCounts = useMemo(
    () => new Map((pendingDrafts ?? []).map((row) => [row.request_id, row.count])),
    [pendingDrafts],
  );

  const renderListItem = useCallback(
    (
      initialRequest: SearchRequestResponse['results']['requests'][0],
      index: number,
      array: SearchRequestResponse['results']['requests'],
    ) => {
      const isTrigger = index === array.length - 3 && !isLastPage;
      return (
        <RequestListItem
          key={initialRequest.id}
          initialRequest={initialRequest}
          previousRequestId={array[index - 1]?.id}
          nextRequestId={array[index + 1]?.id}
          onInView={isTrigger ? showMore : undefined}
          updateRequest={updateRequest}
          hideInactive={hideInactive}
          pendingDraftCount={pendingDraftCounts.get(Number(initialRequest.id)) ?? 0}
        />
      );
    },
    [showMore, updateRequest, hideInactive, isLastPage, pendingDraftCounts],
  );

  return (
    <List bgColor="bg.surface" py="2">
      {items?.map(renderListItem)}
      <Show if={isLastPage}>
        <Center>
          <Stack alignItems="center" textAlign="center" maxW="50%">
            <Text>✈️</Text>
            <Text opacity="0.4" fontSize="xs">
              You've reached your destination.
            </Text>
          </Stack>
        </Center>
      </Show>
    </List>
  );
};
